import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { type IService, Logger, type XY } from "@designboticteam/core";
import { ShapeEditingService } from "@designboticteam/workspace-3d";
import { isInsideCircle, isPolygonInsidePolygon, isPolylineInsidePolygon } from "../../geometry";
import type { BuildingLine } from "../../tools/BuildingLineTool";
import type { Zone } from "../../tools/ZoneTool";
import { Kolega3DStoreService, type ValidationIssue, type ValidationStatus } from "../Kolega3DStoreService";

type ValidatedShape = Zone | BuildingLine;

// Containment failures colour the shape; a check zone still waiting for its building zone must not.
const RED_MARKED_KINDS: ReadonlySet<ValidationIssue["kind"]> = new Set(["outsideProjectArea", "outsideCheckZone"]);

function toRing(shape: ValidatedShape): Array<XY> {
	return shape.getPoints().map((point: THREE.Vector3): XY => [point.x, point.z]);
}

@injectable()
class ValidationService implements IService {
	public readonly serviceName = "Kolega3DValidationService";
	private readonly logger = new Logger(`✅ ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#15803d" });

	private unsubscribers: Array<() => void> = [];

	constructor(
		@inject(Kolega3DStoreService) private readonly kolega3DStoreService: Kolega3DStoreService,
		@inject(ShapeEditingService) private readonly shapeEditingService: ShapeEditingService,
	) {}

	public initialize(): void {
		const { store } = this.kolega3DStoreService;

		this.unsubscribers = [
			store.subscribe((state) => {
				if (state.isValidationDirty) {
					this.validate();
				}
			}),
			this.shapeEditingService.onShapeEdited.subscribe(() => store.setState({ isValidationDirty: true })).unsubscribe,
		];

		this.validate();
		this.logger.log("initialized");
	}

	public dispose(): void {
		for (const unsubscribe of this.unsubscribers) {
			unsubscribe();
		}

		this.unsubscribers = [];
	}

	public validate(): void {
		const { store } = this.kolega3DStoreService;
		const { projectArea, zones, buildingLines } = store.getState();

		const center = projectArea?.getCenter() ?? null;
		const radius = projectArea?.getRadius() ?? 0;
		const isProjectAreaSet = center !== null && radius > 0;

		const shapes: Array<ValidatedShape> = [...zones, ...buildingLines];
		const issues: Array<ValidationIssue> = [];
		const selfIntersecting = new Set<ValidatedShape>();

		for (const shape of shapes) {
			if (shape.getSelfIntersections().length > 0) {
				selfIntersecting.add(shape);
				issues.push({ shape, kind: "selfIntersection" });
			}

			if (isProjectAreaSet && isInsideCircle({ points: shape.getPoints(), center, radius }) === false) {
				issues.push({ shape, kind: "outsideProjectArea" });
			}
		}

		// A self-intersecting ring has no meaningful inside, so containment is only checked on clean shapes.
		const checkZones = zones.filter((zone) => zone.getZoneType() === "CheckZone" && selfIntersecting.has(zone) === false);
		const buildingZones = zones.filter((zone) => zone.getZoneType() === "BuildingZone" && selfIntersecting.has(zone) === false);
		const checkZoneRings = checkZones.map(toRing);
		let areBuildingZonesValid = true;

		for (const zone of zones) {
			const zoneType = zone.getZoneType();

			if (zoneType === "CheckZone" || selfIntersecting.has(zone)) {
				continue;
			}

			const ring = toRing(zone);

			if (checkZoneRings.some((checkZoneRing) => isPolygonInsidePolygon(ring, checkZoneRing)) === false) {
				issues.push({ shape: zone, kind: "outsideCheckZone" });
				areBuildingZonesValid = areBuildingZonesValid && zoneType !== "BuildingZone";
			}
		}

		for (const buildingLine of buildingLines) {
			if (selfIntersecting.has(buildingLine)) {
				continue;
			}

			const line = toRing(buildingLine);

			if (checkZoneRings.some((checkZoneRing) => isPolylineInsidePolygon(line, checkZoneRing)) === false) {
				issues.push({ shape: buildingLine, kind: "outsideCheckZone" });
			}
		}

		const buildingZoneRings = buildingZones.map(toRing);
		let areCheckZonesValid = true;

		for (let index = 0; index < checkZones.length; index++) {
			if (buildingZoneRings.some((ring) => isPolygonInsidePolygon(ring, checkZoneRings[index])) === false) {
				issues.push({ shape: checkZones[index], kind: "noBuildingZoneInside" });
				areCheckZonesValid = false;
			}
		}

		for (const shape of shapes) {
			shape.setInvalid(issues.some((issue) => issue.shape === shape && RED_MARKED_KINDS.has(issue.kind)));
		}

		const hasCheckZone = zones.some((zone) => zone.getZoneType() === "CheckZone");
		const validationStatus: ValidationStatus = {
			isProjectAreaSet,
			hasCheckZone,
			areBuildingZonesValid,
			areCheckZonesValid,
			isValid: isProjectAreaSet && hasCheckZone && issues.length === 0,
		};

		store.setState({ isValidationDirty: false, validationStatus, validationIssues: issues });
	}
}

export { ValidationService };
